import type { Filter } from '../filters';
import type { AnyItem } from './item';
import type { CompositePrimaryKey } from './primary-key';
import { isNil } from '#layers/base';

/******************************************
 * To-Many
 * ****************************************/
export interface ConnectToMany<Key extends CompositePrimaryKey = CompositePrimaryKey> {
	_connect: { keys: Key[] };
}

export interface DisconnectToMany {
	_disconnect: { filter: Filter | CompositePrimaryKey };
}

export interface CreateToMany {
	_create: { data: AnyItem[] };
}

export interface DeleteToMany {
	_delete: { filter: Filter | CompositePrimaryKey };
}

export interface UpdateToMany {
	_update: { filter: Filter | null | CompositePrimaryKey; data: AnyItem };
}

export type RelationOperationToMany = ConnectToMany | DisconnectToMany | CreateToMany | DeleteToMany | UpdateToMany;

export function isConnectToMany(op: RelationOperationToMany): op is ConnectToMany {
	return '_connect' in op && Array.isArray(op._connect.keys);
}
export function isCreateToMany(op: RelationOperationToMany): op is CreateToMany {
	return '_create' in op;
}
export function isUpdateToMany(op: RelationOperationToMany): op is UpdateToMany {
	return '_update' in op;
}
export function isDisconnectToMany(op: RelationOperationToMany): op is DisconnectToMany {
	return '_disconnect' in op && 'filter' in op._disconnect;
}
export function isDeleteToMany(op: RelationOperationToMany): op is DeleteToMany {
	return '_delete' in op;
}

/******************************************
 * To-One
 * ****************************************/

export interface ConnectToOne<Key extends CompositePrimaryKey = CompositePrimaryKey> {
	_connect: { key: Key };
}

interface NestedToOneConnectInCreateOptions<Key extends CompositePrimaryKey = CompositePrimaryKey> {
	key: Key;
	mode: 'create';
}

interface NestedToOneConnectInUpdateOptions<Key extends CompositePrimaryKey = CompositePrimaryKey> {
	key: Key;
	mode: 'update';
}

export interface DisconnectToOne {
	_disconnect: Record<string, never>;
}
export interface CreateToOne {
	_create: { data: AnyItem };
}
export interface UpdateToOne {
	_update: { filter?: Filter; data: AnyItem };
}

export function isConnectToOne(op: RelationOperationToOne | null): op is ConnectToOne {
	return !isNil(op) && '_connect' in op && 'key' in op._connect;
}
export function isCreateToOne(op: RelationOperationToOne | null): op is CreateToOne {
	return !isNil(op) && '_create' in op && 'data' in op._create;
}
export function isUpdateToOne(op: RelationOperationToOne | null): op is UpdateToOne {
	return !isNil(op) && '_update' in op && 'data' in op._update;
}
export function isDisconnectToOne(op: RelationOperationToOne | null): op is DisconnectToOne {
	return !isNil(op) && '_disconnect' in op && Object.keys(op._disconnect).length === 0;
}

export function nestedToOneConnect<Key extends CompositePrimaryKey>(options: NestedToOneConnectInCreateOptions<Key>): ConnectToOne<Key>;
export function nestedToOneConnect<Key extends CompositePrimaryKey>(options: NestedToOneConnectInUpdateOptions<Key>): ConnectToOne<Key>[];
export function nestedToOneConnect<Key extends CompositePrimaryKey>({ key, mode }: NestedToOneConnectInCreateOptions<Key> | NestedToOneConnectInUpdateOptions<Key>): ConnectToOne<Key> | ConnectToOne<Key>[] {
	const connect = { _connect: { key } };

	if (mode === 'create') return connect;
	return [connect];
}

/******************************************
 * General
 * ****************************************/

export type RelationOperationToOne = RelationOperationToOneInCreate | RelationOperationToOneInUpdate;

type SingleToOneOperation = DisconnectToOne | UpdateToOne | ConnectToOne | CreateToOne;
export type RelationOperationToOneInCreate = SingleToOneOperation;
export type RelationOperationToOneInUpdate = SingleToOneOperation[];

export interface RelationHandlers {
	handleCreate: (payload: AnyItem) => void;
	handleUndoCreate: (payload: AnyItem) => void;
	handleConnect: (key: CompositePrimaryKey) => void;
	handleUndoConnect: (key: CompositePrimaryKey) => void;
	handleDisconnect: (key: CompositePrimaryKey) => void;
	handleUndoDisconnect: (key: CompositePrimaryKey) => void;
	handleDelete: (key: CompositePrimaryKey) => void;
	handleUndoDelete: (key: CompositePrimaryKey) => void;
	handleUpdate: (originalKey: CompositePrimaryKey | null, payload: AnyItem) => void;
}
